import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { Link } from '@/components/link/link';
import { sanityClient } from '@/content/sanity/sanity-client/sanity-client';
import { PortableText } from '@portabletext/react';

const MainDiv = styled.div`
  height: auto;
  width: 100%;
  padding: 13% 12.5% calc(10.51% - 32px) 12.5%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background: ${({ theme }) => theme.palette.common.midnight};
  @media (max-width: 768px) {
    padding: 98px 20px;
    min-height: 100vh;
  }
`;
const Title = styled(Typography)`
  font-family: var(--font-cooper-light);
  font-style: normal;
  font-weight: 300;
  font-size: 50px;
  line-height: 109.3%;
  color: #ffffff;
  margin-bottom: 50px;
`;
const Subtitle = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: 500;
  font-size: 22px;
  line-height: 160%;
  color: #ffffff;
  margin-bottom: 25.5px;
  & a {
    color: #4497aa;
    text-decoration: none;
  }
`;
const Text = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 160%;
  color: #ffffff;
  margin-bottom: 25.5px;
  & a {
    color: #4497aa;
    text-decoration: none;
  }
`;
const Parts = styled(Typography)`
  font-family: var(--font-cooper-light);
  font-style: normal;
  font-weight: normal;
  font-size: 30px;
  line-height: 160%;
  color: #ffffff;
  margin-bottom: 25.5px;
`;

interface Subsection {
  subtitle: string;
  text: any[];
}

interface Section {
  partTitle: string;
  subsections: Subsection[];
}

interface PoliciesDocument {
  _id: string;
  title: string;
  sections: Section[];
}

const CookiesPage = () => {
  const [policiesData, setPoliciesData] = useState<PoliciesDocument | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchPolicies = async () => {
      const data = await sanityClient.fetch(
        `*[_type=="policies" && title == "Cookies Policy"][0]{ _id, title, sections[]{ partTitle, subsections[]{ subtitle, text } } }`,
      );
      setPoliciesData(data);
      setLoading(false);
    };

    fetchPolicies();
  }, []);

  if (loading || !policiesData) {
    return <MainDiv></MainDiv>;
  }

  return (
    <MainDiv>
      <Title>{policiesData.title}</Title>
      {policiesData.sections.map((section, idx) => (
        <div key={idx}>
          <Parts>{section.partTitle}</Parts>
          {section.subsections.map((subsec, subidx) => (
            <div key={subidx}>
              <Subtitle>{subsec.subtitle}</Subtitle>
              <Text>
                <PortableText
                  value={subsec.text}
                  components={{
                    marks: {
                      link: ({ children, value }: any) => <Link href={value.href}>{children}</Link>,
                      strong: ({ children }: any) => <strong>{children}</strong>,
                    },
                    block: {
                      normal: ({ children }: any) => <p>{children}</p>,
                    },
                  }}
                />
              </Text>
            </div>
          ))}
        </div>
      ))}
    </MainDiv>
  );
};

export default CookiesPage;
